import { useState } from "react";
import { toast } from "sonner";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { api, errMsg, money, dateTime } from "@/lib/api";
import { Card, StatusBadge, Btn, FieldLabel, inputCls } from "./ui";

export default function PaymentCard({ o, onChange }) {
  const remaining = Math.round((o.total - (o.refunded_amount || 0)) * 100) / 100;
  const [amount, setAmount] = useState(remaining.toFixed(2));
  const [restock, setRestock] = useState(!o.restocked);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const canRefund = ["paid", "partially_refunded"].includes(o.payment_status) && remaining > 0 && o.payment_provider !== "paypal";

  const refund = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/orders/${o.id}/refund`, { amount: Number(amount), restock });
      toast.success("Refund sent to the customer");
      onChange(data);
    } catch (e) {
      toast.error(errMsg(e));
    }
    setBusy(false);
    setOpen(false);
  };

  return (
    <Card title="Payment" testid="order-payment-card">
      <div className="text-sm space-y-1.5">
        <div className="flex justify-between"><span className="text-zinc-500">Status</span><StatusBadge status={o.payment_status} testid="order-payment-status" /></div>
        <div className="flex justify-between"><span className="text-zinc-500">Method</span><span>{o.payment_provider === "paypal" ? "PayPal" : "Card (Stripe)"}</span></div>
        {o.paid_at && <div className="flex justify-between"><span className="text-zinc-500">Paid</span><span>{dateTime(o.paid_at)}</span></div>}
        {o.transaction_id && <div className="flex justify-between gap-3"><span className="text-zinc-500">Transaction ID</span><span className="font-mono text-xs break-all text-right" data-testid="order-transaction-id">{o.transaction_id}</span></div>}
        {o.refunded_amount > 0 && <div className="flex justify-between"><span className="text-zinc-500">Refunded</span><span data-testid="order-refunded-amount">{money(o.refunded_amount)}</span></div>}
      </div>
      {o.refunds?.length > 0 && (
        <ul className="mt-3 pt-3 border-t border-zinc-100 space-y-1 text-xs text-zinc-600" data-testid="order-refund-history">
          {o.refunds.map((r) => <li key={r.id} className="flex justify-between"><span>{dateTime(r.created_at)} · {r.status}</span><span>−{money(r.amount)}</span></li>)}
        </ul>
      )}
      {canRefund && (
        <div className="mt-4 pt-4 border-t border-zinc-200 space-y-3">
          <label className="block"><FieldLabel hint={`(up to ${money(remaining)})`}>Refund amount</FieldLabel>
            <input type="number" min="0.01" step="0.01" max={remaining} className={inputCls} value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="refund-amount-input" />
          </label>
          {!o.restocked && (
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={restock} onChange={(e) => setRestock(e.target.checked)} className="accent-zinc-950 w-4 h-4" data-testid="refund-restock-check" /> Put items back in stock</label>
          )}
          <Btn variant="danger" className="w-full" onClick={() => setOpen(true)} disabled={busy || !(Number(amount) > 0)} data-testid="refund-button">Refund</Btn>
        </div>
      )}
      {o.payment_provider === "paypal" && o.payment_status === "paid" && <p className="text-xs text-zinc-500 mt-3">PayPal refunds are made in your PayPal account.</p>}
      {o.emails?.length > 0 && (
        <div className="mt-4 pt-3 border-t border-zinc-100 text-xs text-zinc-600 space-y-1" data-testid="order-email-log">
          <p className="font-medium text-zinc-800">Emails to customer</p>
          {o.emails.map((e, i) => <p key={i}>{e.kind === "confirmation" ? "Order confirmation" : "Shipping update"} · {e.status} · {dateTime(e.at)}</p>)}
        </div>
      )}
      <AlertDialog open={open} onOpenChange={setOpen}>
        <AlertDialogContent className="admin bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Refund {money(Number(amount) || 0)}?</AlertDialogTitle>
            <AlertDialogDescription>The money goes back to the customer's card. This can't be undone.{restock && !o.restocked ? " Items will be put back in stock." : ""}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="refund-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={refund} className="bg-red-600 hover:bg-red-700" data-testid="refund-confirm">Refund</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
